import type { SentryModule } from "@lib/telemetry/sentry";
import { getClientBaseUrl } from "@client/trpc";
import { getEnvironment } from "@client/utils/environment";
import { attachPostHogSessionReplayUrl } from "@client/utils/sentryPosthogLink";

const cefSharpObjectNotFoundRejection =
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+$/;

/**
 * The Sentry configuration shared by both entries — the web `main.tsx` and
 * the native `main-mobile.tsx` — so the installed iPad/iPhone app reports
 * crashes exactly as the web app does. Takes the loaded SDK because both
 * entries initialize through `initializeSentry`, which defers the SDK chunk
 * off the startup closure.
 */
export function buildSentryInitOptions(
  Sentry: SentryModule,
): Parameters<SentryModule["init"]>[0] {
  return {
    dsn: "https://831a4844c1ea93d5a69d81a4b38b2338@o4509074795397120.ingest.us.sentry.io/4509135602450432",
    // Deliberately no Session Replay: PostHog records every session, and a
    // second rrweb recorder doubles the per-interaction overhead. beforeSend
    // links each error to its PostHog recording instead.
    integrations: [
      Sentry.browserTracingIntegration({
        // Drop the per-asset waterfall from the pageload transaction. Sentry
        // rebuilds every PerformanceResourceTiming entry as its own span when
        // the pageload span ends, which on a funder workspace load is ~75 of
        // them (the entry bundle, the route chunks the background warm pulls
        // in, CSS, fonts, posters). Two costs: the flush is a single ~13ms
        // main-thread task, and the resulting envelope exceeded the tunnel's
        // body limit, so the whole transaction was rejected with a 413 and no
        // pageload tracing landed at all (see the tunnel's limit in app.ts).
        //
        // What we keep is the part worth monitoring: the pageload/navigation
        // transactions themselves, their web vitals (LCP/FCP/CLS/TTFB), and
        // the navigation-timing breakdown. `fetch` and `xhr` are absent below
        // because Sentry already excludes them here — our tRPC calls keep
        // their spans through the request instrumentation instead. For a
        // per-asset waterfall, take a DevTools performance trace; it's a
        // better tool for that question than sampled production spans.
        ignoreResourceSpans: [
          "resource.script",
          "resource.link", // modulepreload + stylesheet <link>s
          "resource.css", // @font-face and url() loads made from CSS
          "resource.img",
          "resource.video",
          "resource.other",
        ],
      }),
    ],
    tracesSampleRate: 1.0,
    // Requests to our own API hosts carry sentry-trace/baggage so server-side
    // spans join the browser's trace (the server adopts them — see
    // `continueTrace` in server/app.ts). From the native app these requests
    // are cross-origin, which is fine: the CORS preflight reflects requested
    // headers.
    tracePropagationTargets: [/^https:\/\/(staging.)?s-process\.app/],
    environment: getEnvironment(),
    ignoreErrors: [
      // CefSharp-based link scanners can reject with this exact shape during
      // email/security prefetches. It is not caused by S-Process application code.
      cefSharpObjectNotFoundRejection,
    ],
    beforeSend(event) {
      return attachPostHogSessionReplayUrl(event);
    },
    normalizeDepth: 10,
    // In the native WebView the document origin is `capacitor://localhost`, so
    // a relative tunnel path would post envelopes into the app bundle and drop
    // every event silently. getClientBaseUrl() is how tRPC solves the same
    // problem: "" on the web (keeping the tunnel same-origin, as before) and
    // the deployed API host on bundled native builds.
    tunnel: `${getClientBaseUrl()}/sentry-tunnel`,
  };
}
